import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, formatDate } from 'lib/utils';
import { BuySeatsDialog } from 'features/iam/components/seats/buy-seats-dialog';
import { PlanActions, planStateLabel, planUnavailableNote, seatsLabel } from 'features/iam/components/seats/plan-actions';
import { formatMinor, PlanSeats, seatPriceLabel } from 'features/subscription/utils/seat-pools';
import {
  accountBadgeClassName,
  accountOutlineButtonClassName,
  accountSectionClassName,
  accountSectionTitleClassName,
} from './account-styles';

/**
 * "6 of 7 seats in use · CHF 19.90 per seat / month", "No seats · …" for a plan not bought, or
 * "Ends on Dec 1, 2026. 6 people move to no plan then." for a cancelled one.
 */
const planSummary = (t: (key: string, options?: Record<string, unknown> | string) => string, entry: PlanSeats) => {
  if (entry.cancelScheduled) {
    const ends = t('PLAN_ENDS_ON_DATE', { date: formatDate(entry.periodEndUtc), defaultValue: 'Ends on {{date}}.' });
    if (entry.held === 0) return ends;
    const moving =
      entry.held === 1
        ? t('ONE_PERSON_MOVES_TO_NO_PLAN', '1 person moves to no plan then.')
        : t('PEOPLE_MOVE_TO_NO_PLAN', { count: entry.held, defaultValue: '{{count}} people move to no plan then.' });
    return `${ends} ${moving}`;
  }
  let seats = t('NO_SEATS', 'No seats');
  if (entry.awaitingPayment) seats = seatsLabel(t, entry.purchased);
  else if (entry.pool) {
    seats = t('SEATS_IN_USE_OF', {
      held: entry.held,
      total: entry.held + entry.freeThisPeriod,
      defaultValue: '{{held}} of {{total}} seats in use',
    });
  }
  const price = seatPriceLabel(t, entry, true);
  return price ? `${seats} · ${price}` : seats;
};

/** Every plan the organization can hold, what it costs a period, and the admin's actions on it. */
export function OrgPlansCard({ planSeats }: { planSeats: PlanSeats[] }) {
  const { t } = useTranslation();
  const [buying, setBuying] = useState<{ focusCode?: string } | null>(null);
  const renewDate = planSeats.find((entry) => entry.pool && !entry.awaitingPayment)?.periodEndUtc;

  return (
    <section className={accountSectionClassName}>
      <div className="flex flex-col gap-3 px-6 pb-1 pt-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className={accountSectionTitleClassName}>
            {t('ORGANIZATION_PLANS', 'Plans in your organization')}
          </h2>
          {renewDate && (
            <p className="text-sm leading-body text-grey-500 dark:text-muted-foreground">
              {t('SEATS_RENEW_ON_CANCEL_NOTE', {
                date: formatDate(renewDate),
                defaultValue: 'Seats renew on {{date}}. Cancelling stops a plan on that date, not today.',
              })}
            </p>
          )}
        </div>
        <button
          type="button"
          className={cn(accountOutlineButtonClassName, 'self-start')}
          onClick={() => setBuying({})}
        >
          {t('MANAGE_SEATS', 'Manage seats')}
        </button>
      </div>

      <ul className="px-6 pb-2">
        {planSeats.map((entry) => {
          const isLive = !!entry.pool && !entry.awaitingPayment;
          const note = planUnavailableNote(t, entry);
          // What the plan costs a period: its seats at the next renewal times the seat price, as on the next bill.
          const periodTotal =
            isLive && entry.unitAmountMinor
              ? `${formatMinor(entry.seatsNextPeriod * entry.unitAmountMinor, entry.currencyCode)}${
                  entry.interval ? ` / ${t(`INTERVAL_${entry.interval.toUpperCase()}`, entry.interval.toLowerCase())}` : ''
                }`
              : '';

          return (
            <li
              key={entry.code}
              className="flex flex-col gap-3 border-b border-grey-100 py-3.5 last:border-b-0 dark:border-border sm:flex-row sm:items-center sm:gap-6"
            >
              <div className="flex min-w-0 flex-col gap-0.5 sm:flex-1">
                <p className="text-sm font-medium leading-body text-grey-950 dark:text-foreground">{entry.name}</p>
                <p className="text-xs leading-heading text-grey-500 dark:text-muted-foreground">{planSummary(t, entry)}</p>
                {note && <p className="text-xs leading-heading text-grey-500 dark:text-muted-foreground">{note}</p>}
              </div>

              <div className="flex flex-wrap items-center gap-4 sm:justify-end">
                {entry.cancelScheduled && (
                  <span
                    className={cn(accountBadgeClassName, 'bg-error-50 text-error-700 dark:bg-error-700/20 dark:text-error-500')}
                  >
                    {t('CANCELLED', 'Cancelled')}
                  </span>
                )}
                {entry.pool && !entry.cancelScheduled && (
                  <span
                    className={cn(
                      accountBadgeClassName,
                      'border border-grey-100 text-grey-600 dark:border-border dark:text-muted-foreground'
                    )}
                  >
                    {planStateLabel(t, entry)}
                  </span>
                )}
                {periodTotal && (
                  <span
                    className={cn(
                      'text-sm leading-body',
                      entry.cancelScheduled ? 'text-grey-500 dark:text-muted-foreground' : 'text-grey-950 dark:text-foreground'
                    )}
                  >
                    {periodTotal}
                  </span>
                )}
                {entry.pool ? (
                  <PlanActions entry={entry} />
                ) : (
                  <button
                    type="button"
                    className={cn(accountOutlineButtonClassName, 'min-w-32 whitespace-nowrap')}
                    onClick={() => setBuying({ focusCode: entry.code })}
                  >
                    {t('BUY_SEATS', 'Buy seats')}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <BuySeatsDialog
        open={!!buying}
        // A plan row opens on its own plan; every plan stays in the list.
        planSeats={planSeats}
        focusCode={buying?.focusCode}
        onClose={() => setBuying(null)}
      />
    </section>
  );
}
